import { Scope, BaseScopes } from "./scopes";
import {
    BadRequestArgsError,
    UnauthorizedError,
    ForbiddenError,
    NotFoundError,
    InternalServerError,
    UserNonexistentError,
    ExpiredTokenError
} from "./errors/networkError";
import type { User } from "./user";

// const GS_API_URL: string = `https://gs.rll${process.env.NODE_ENV === 'production' ? '' : '-dev'}.byu.edu/`;
export const GS_API_URL: string = `http://localhost:8000/`;

export abstract class GSEndpoint<ReqSchema = {}, ResSchema = {}, QuerySchema = {}> {
    path: string;
    queryParams?: QuerySchema;
    urlParams?: string[];
    method: "GET" | "POST" | "DELETE" | "PUT" | "PATCH";
    publicEndpoint: boolean;
    orScopes: Set<Scope>;
    andScopes?: Set<Scope>;
	gs_api_url: string;

    constructor(path: string, 
                method: "GET" | "POST" | "DELETE" | "PUT" | "PATCH", 
                orScopes: Scope[], 
                queryParams?: QuerySchema, 
                urlParams?: string[],
                andScopes?: Scope[],
    ) {
        this.path = path;
        this.queryParams = queryParams;
        this.urlParams = urlParams;
        this.method = method;
        this.publicEndpoint = orScopes.length === 0 && (andScopes ? andScopes.length === 0 : true);
        this.orScopes = new Set(orScopes);
        this.orScopes.add(BaseScopes.ADMIN); // admin has access to all endpoints
        this.andScopes = andScopes ? new Set(andScopes) : undefined;
		this.gs_api_url = this.getGrowthSpurtUrl() + "/";
    }

	/**
	 * Gets the URL for the Growth Spurt API.
	 *
	 * @returns {("https://gs.rll.byu.edu" | "https://gs.rll-dev.byu.edu" | "http://localhost:8000")}
	 */
	getGrowthSpurtUrl(): ("https://gs.rll.byu.edu" | "https://gs.rll-dev.byu.edu" | "http://localhost:8000") {
		let currentUrl = window.location.href;
		if (currentUrl.includes("rll-dev")) {
			return "https://gs.rll-dev.byu.edu";
		}
		if (currentUrl.includes("localhost")) {
			return "http://localhost:8000"
		}
		return "https://gs.rll.byu.edu";
	}

    async makeRequest(req: ReqSchema, user: Partial<User>, headers?: Record<string, string>): Promise<ResSchema> {
        const newUser = { 
            id: user.id ?? "",
            name: user.name ?? "",
            email: user.email ?? "",
            permissions: user.permissions ? user.permissions : new Set<string>(),
            authToken: user.authToken ?? ""
        } as User;
        if (!this.publicEndpoint) {
            if (!newUser.authToken) throw new UnauthorizedError("No auth token found. Please log in.");
            if (newUser.permissions.intersection(this.orScopes).size === 0) {
                throw new ForbiddenError("User does not have permission to access this information.");
            }
            if (!(this.andScopes ?? new Set()).isSubsetOf(newUser.permissions)) {
                throw new ForbiddenError("User does not have all required permissions to access this information.");
            }
        }
        let url = this.gs_api_url + this.path;

        // url params
        let matchIdx = 0;
        for (const match of url.matchAll(/\{[\w_-]+\}/g)) {
            if (!this.urlParams || matchIdx >= this.urlParams.length) {
                throw new BadRequestArgsError(`Missing URL parameter for ${match[0]}`);
            }
            url = url.replace(match[0], this.urlParams[matchIdx]);
            matchIdx++;
        }

        // query params
        if (this.queryParams) {
            let urlParams = new URLSearchParams();
            for (const [key, value] of Object.entries(this.queryParams)) {
                urlParams.set(key, String(value));
            }
            url += `?${urlParams.toString()}`;
        }

        // return promise, lowercase headers still overwrite
        const finalHeaders: Record<string,string> = {
            "Content-Type": headers && headers["content-type"] ? headers["content-type"] : "application/json",
            "Authorization": headers && headers["authorization"] ? headers["authorization"] : `Bearer ${newUser.authToken}`,
            ...headers
        }

        let body;
        if (req instanceof FormData) {
            delete finalHeaders["Content-Type"];
            body = req;
        } else {
            body = this.method !== "GET" ? JSON.stringify(req) : undefined;
        }
        const res = await fetch(url, {
            method: this.method,
            headers: finalHeaders,
            body: body
        });
        if (res.ok) {
            const b = await res.json();
            return (b ?? {}) as ResSchema;
        } else {
            return await this.handleError(res);
        }
    }

    abstract request?(req: ReqSchema, user: User): Promise<any>;

    async handleError(errRes: Response): Promise<never> {
        console.error(`Error in endpoint ${this.path} with status ${errRes.status}:`, errRes);
        if (errRes.status === 400) throw new BadRequestArgsError("Bad request arguments");
        if (errRes.status === 401) {
            const body = await errRes.json();

            switch (body.detail) {
                case "Error in FamilySearch authentication": // TODO: update with backend
                    throw new ExpiredTokenError("Authentication token was bad or expired. Please log in again.");
                case "No GrowthSpurt account for user":
                    throw new UserNonexistentError("No GrowthSpurt account found for user. Please create an account.");
                default:
                    throw new UnauthorizedError("Unauthorized");
            }
        }
        if (errRes.status === 403) throw new ForbiddenError("Forbidden");
        if (errRes.status === 404) throw new NotFoundError("Not found");
        if (errRes.status === 500) throw new InternalServerError("Internal server error");
        throw new Error(`Unexpected error in endpoint ${this.path}: ${errRes.statusText}`);
    }
}
